import {
  Stack,
  Center,
  Loader,
  FlexProps,
  StackProps,
  Box,
} from "@mantine/core"
import { useDebouncedValue, useIntersection } from "@mantine/hooks"
import { Input, Output } from "../../../@client/api"
import { PaginatedData } from "../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { ScrollContainer } from "../../../-components/scroll-container"
import { useInfiniteGet } from "../../../typed-client/hooks/use-infinite-get"
import { ExclusiveProperties } from "../../../utils/exclusive-properties"
import { v4 as uuid } from "uuid"

const pageSize = 100

type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]

type PaginatedListUrls = KeysOfType<Output, PaginatedData<unknown>>

export interface PaginatedListRequest {
  firstItem: number
  pageSize: number
}

interface Props<URL extends PaginatedListUrls> {
  url: URL
  renderItem: (item: Output[URL]["data"][0]) => React.ReactNode
  activtiyTypeFilter?: string[]
  noFilter?: boolean
  partialInput: ExclusiveProperties<Input[URL], PaginatedListRequest>
  flexProps?: FlexProps
  stackProps?: StackProps
}

export function InfinitScrollingList<URL extends PaginatedListUrls>({
  url,
  renderItem,
  partialInput,
  flexProps,
  stackProps,
}: Props<URL>) {
  const [debouncedInput] = useDebouncedValue(partialInput, 500)

  const {
    data,
    refetch,
    fetchNextPage,
    isRefetching,
    status,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteGet(
    url,
    (params) =>
      ({
        firstItem: params.after ?? 0,
        pageSize: params.take ?? pageSize,
        ...debouncedInput,
      }) as Input[URL],
    (lastPage) => ({
      after: lastPage.firstItem + pageSize,
      before: lastPage.firstItem,
      take: pageSize,
    })
  )

  const { ref, entry } = useIntersection()

  React.useEffect(() => {
    refetch()
  }, [debouncedInput])

  React.useEffect(() => {
    if (
      entry?.isIntersecting &&
      data?.pages[data?.pages.length - 1].hasNextPage
    )
      fetchNextPage()
  }, [status, fetchNextPage, entry?.isIntersecting])

  return (
    <Center h={"100%"} w={"100%"} style={{ overflow: "hidden" }}>
      <Stack
        h={"100%"}
        w={"100%"}
        maw={400}
        style={{ overflow: "hidden" }}
        {...stackProps}
      >
        <ScrollContainer gap={"xs"} {...flexProps}>
          {isRefetching || isLoading ? (
            <Center>
              <Loader size={30} />
            </Center>
          ) : (
            data?.pages.flatMap((pages) =>
              pages.data.flatMap((item) => (
                <Box key={uuid()}>{renderItem(item)}</Box>
              ))
            )
          )}
          <div style={{ height: 10 }} ref={ref}>
            {isFetchingNextPage && (
              <Center>
                <Loader size={30} />
              </Center>
            )}
          </div>
        </ScrollContainer>
      </Stack>
    </Center>
  )
}
